// Post-test review for one English topic block: what the student was asked, what they put, and
// how each part was marked.
//
// The English counterpart of BmdAttemptReviewModal, and a separate component for the same reason
// the question views are a deliberate fork: a maths diagnostic is one question, one choice from a
// list, one verdict, so that modal renders an answer list with "Selected" markers. An English
// question is one of sixteen types whose response is a JSON object keyed by part, and a reading
// comprehension is marked once PER SUB-QUESTION. Flattening that into the maths shape would throw
// away the part a verdict belongs to - which is the only grain at which English knows which skill
// failed.
//
// The question itself is drawn by EngDiagQuestionView, the same component the preview modal and
// the student app use, with the student's own answers passed in as its value. So the tutor sees
// the question as the child saw it, filled in as the child filled it in, rather than a dump of
// response JSON.

import React from "react"
import { Modal, ModalBody, ModalHeader, Spinner } from "reactstrap"
import { EngDiagQuestionView } from "./EngDiagQuestionView"
import { getEngTopicAttemptDetail } from "../services/eng-report.service"
import type {
  EngReportAnswer,
  EngReportReviewQuestion,
  GetEngTopicAttemptDetailResponse,
} from "../services/eng-report.service"
import type {
  EngDiagQuestion,
  EngDiagStudentAnswers,
} from "../types/eng-diag.types"

/**
 * Phase headings. English has no FM_RETRY - there is no fundamental-misconception retry in the
 * English flow - and RETRY is a question the student comes back to after a skip.
 */
const PHASE_LABELS: Record<string, string> = {
  BASE: "Base questions",
  RETRY: "Returned to after skipping",
}

function phaseLabel(phase: string): string {
  if (PHASE_LABELS[phase]) return PHASE_LABELS[phase]
  if (phase.startsWith("PREREQ_L")) {
    return `Prerequisite chain, level ${phase.slice("PREREQ_L".length)}`
  }
  return phase
}

/** `punctuation_insert` reads better as "punctuation insert" than as a column name. */
function typeLabel(type: string): string {
  return type.replace(/_/g, " ")
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

/**
 * The student's answers, ready for the question view.
 *
 * Every attempt row of one submission carries the WHOLE response object, not just its own part,
 * so any row would do - but they are merged rather than one being picked, because a part answered
 * in a later submission has its own row and its own response, and the newest value for each key
 * is the one the student left behind.
 */
function answersFor(question: EngReportReviewQuestion): EngDiagStudentAnswers {
  const merged: EngDiagStudentAnswers = {}
  for (const answer of question.answers) {
    const response = answer.response
    if (!response || typeof response !== "object") continue
    for (const [key, value] of Object.entries(response as Record<string, unknown>)) {
      if (value == null) continue
      merged[key] = String(value)
    }
  }
  return merged
}

/**
 * The question as EngDiagQuestionView wants it.
 *
 * `yearId` is required by the type but never read by the renderer - the year a question belongs
 * to is tutor context, and the view deliberately shows the student nothing of it - so the
 * snapshot not carrying one costs nothing.
 */
function toQuestionView(question: EngReportReviewQuestion): EngDiagQuestion {
  return {
    id: question.snapshot.question_id,
    type: question.snapshot.type as EngDiagQuestion["type"],
    yearId: 0,
    instruction: question.snapshot.instruction || undefined,
    content: question.snapshot.content,
    options: question.snapshot.options,
    isManuallyMarked: question.is_manually_marked,
  }
}

/**
 * What a part is called, in the author's words where they exist.
 *
 * `target_ref` is an id - a sub-question's, a blank's, a field's - which says nothing to a tutor.
 * The snapshot holds the authored text for every one of those, so this looks the id up and falls
 * back to the raw ref rather than inventing a number that matches nothing on screen.
 */
/** What to call part N of each authored list, when the part carries no label of its own. */
const PART_NOUN: Record<string, string> = {
  subQuestions: "Question",
  blanks: "Blank",
  fields: "Field",
  pairs: "Pair",
  items: "Item",
  sites: "Position",
}

function partLabel(question: EngReportReviewQuestion, targetRef: string): string {
  if (!targetRef) return "Whole question"
  const options = question.snapshot.options as Record<string, unknown>
  const lists: Array<[string, string[]]> = [
    ["subQuestions", ["prompt"]],
    ["blanks", ["label", "id"]],
    ["fields", ["label"]],
    ["pairs", ["leftText"]],
    ["items", ["text"]],
    ["sites", ["id"]],
  ]
  for (const [listName, textKeys] of lists) {
    const list = options?.[listName]
    if (!Array.isArray(list)) continue
    const index = list.findIndex(
      entry => entry && typeof entry === "object" && (entry as any).id === targetRef,
    )
    if (index < 0) continue
    const match = list[index] as Record<string, unknown>
    for (const key of textKeys) {
      const text = match[key]
      if (typeof text === "string" && text.trim()) return text
    }
    // Authored parts carry no label of their own on most types - a `blank` is just
    // {id, answer} - so fall back to the POSITION, which a tutor can at least find on the
    // question. A single-part question has no position worth naming.
    return list.length === 1 ? "Answer" : `${PART_NOUN[listName] ?? "Part"} ${index + 1}`
  }
  // An id nothing in the snapshot matches. Naming it would print a key like "ThKFZvLUxC-T"
  // next to the verdict, which is what this used to do.
  return "Answer"
}

/** Right, wrong, or neither-yet. A null verdict on a settled answer means a tutor still owes it. */
const VerdictBadge: React.FC<{ answer: EngReportAnswer }> = ({ answer }) => {
  if (answer.skip_reason) {
    return (
      <span className="badge bg-warning text-dark">
        {answer.skip_reason === "TIMEOUT" ? "Ran out of time" : "Skipped"}
      </span>
    )
  }
  if (answer.marking_status === "PENDING" || answer.is_correct === null) {
    return <span className="badge bg-secondary">Awaiting marking</span>
  }
  return answer.is_correct ? (
    <span className="badge bg-success">Correct</span>
  ) : (
    <span className="badge bg-danger">Incorrect</span>
  )
}

/**
 * What the author expected, ready to draw.
 *
 * An image rather than a string because 136 MCQ choices in the bank are a bare `<img>` inside a
 * paragraph - typed "text", with no alt text - so stripping the HTML leaves nothing at all. 37
 * of those are the marked answer, which would have rendered as a blank "Expected:" line.
 */
type ExpectedAnswer = (
  | { kind: "text"; text: string }
  | { kind: "image"; src: string }
  | { kind: "audio"; name: string }
) & {
  /**
   * Which option it was, A-first, for a question whose choices are a list.
   *
   * The letter is what makes an image or a clip findable: the choices are drawn above, and
   * "Option C" points straight at one. The order is the AUTHORED order, which is what this
   * modal renders - the student saw them shuffled, and their own pick is highlighted by id, so
   * the letter describes what the tutor is looking at, not what the child was.
   */
  option?: string
}

/** Authored HTML down to the words, for a choice whose content is rich text. */
const asText = (html: unknown): string =>
  String(html ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim()

const asExpected = (text: string | null): ExpectedAnswer | null =>
  text ? { kind: "text", text } : null

/** Authored rich text: its words, or the image or clip it turns out to be, or nothing. */
function fromHtml(html: unknown): ExpectedAnswer | null {
  const raw = String(html ?? "")
  const text = asText(raw)
  if (text) return { kind: "text", text }
  const src = /<img[^>]+src\s*=\s*["']([^"']+)["']/i.exec(raw)
  if (src) return { kind: "image", src: src[1] }
  // An audio node carries no text at all. Three "choose the beginning sound" questions have
  // clips for choices, so the file name is the only thing that names which one was right.
  const label = /data-label\s*=\s*["']([^"']+)["']/i.exec(raw)
  const file = /data-file-name\s*=\s*["']([^"']+)["']/i.exec(raw)
  if (label || file) return { kind: "audio", name: (label ?? file)![1] }
  return null
}

/** The same, plus the A-first position of the choice among its siblings. */
function fromChoice(choices: unknown, pick: (c: any) => boolean): ExpectedAnswer | null {
  if (!Array.isArray(choices)) return null
  const index = choices.findIndex((c) => pick(c))
  if (index < 0) return null
  const found = fromHtml((choices[index] as any)?.content ?? (choices[index] as any)?.text)
  return found ? { ...found, option: String.fromCharCode(65 + index) } : null
}

/** Several accepted values read as alternatives; a value's own newlines are not a list. */
const joinAccepted = (values: unknown[]): string =>
  values
    .map((v) => asText(v).replace(/\n+/g, " / "))
    .filter(Boolean)
    .join("  or  ")

const find = (list: unknown, id: string): Record<string, unknown> | null => {
  if (!Array.isArray(list)) return null
  const hit = list.find((e) => e && typeof e === "object" && (e as any).id === id)
  return (hit as Record<string, unknown>) ?? null
}

/** The one entry of a list, when a question has exactly one part and no target to match on. */
const only = (list: unknown): Record<string, unknown> | null =>
  Array.isArray(list) && list.length === 1 && list[0] && typeof list[0] === "object"
    ? (list[0] as Record<string, unknown>)
    : null

/** Accepted values off an `answer: [{value}]` list, the shape blanks and open fields share. */
const acceptedValues = (part: Record<string, unknown> | null): string | null => {
  const answers = part?.answer
  if (!Array.isArray(answers)) return null
  return joinAccepted(answers.map((a) => (a as any)?.value)) || null
}

/**
 * What the author expected, for one part of one question.
 *
 * WHY THIS EXISTS. The modal showed the student's answer and a red "Incorrect" and nothing
 * else, so correct marking read as broken: a rebuilt sentence that scans perfectly is wrong
 * because it used a distractor word, and "I" is the wrong pick for "which word needs a capital"
 * because the answer is "sam". Neither is visible without the answer key beside it.
 *
 * The key IS available here: buildTopicAttemptDetail selects the authored `options` straight
 * from eng_fdiag_question, unlike the student payload, which goes through stripOptions.
 *
 * Verified against every question in the bank: all 12 types resolve for every part.
 *
 * Returns null where there is nothing to show - a type with no authored answer, or a part the
 * snapshot does not describe - rather than a guess.
 */
function expectedAnswer(
  question: EngReportReviewQuestion,
  targetRef: string,
): ExpectedAnswer | null {
  const o = (question.snapshot.options ?? {}) as Record<string, any>
  switch (question.snapshot.type) {
    case "blank":
      return asExpected(acceptedValues(find(o.blanks, targetRef) ?? only(o.blanks)))
    case "open_ended":
      return asExpected(acceptedValues(find(o.fields, targetRef) ?? only(o.fields)))
    case "mcq":
      return fromChoice(o.choices, (c) => c?.isAnswer)
    case "mc_cloze": {
      const blank = find(o.blanks, targetRef) ?? only(o.blanks)
      return fromChoice(blank?.choices, (c) => c?.isAnswer)
    }
    case "sentence_rebuild": {
      // Word ids in the authored order. Any word NOT in that list is a distractor, which is how
      // a sentence that reads perfectly can still be wrong.
      const byId = new Map((o.words ?? []).map((w: any) => [w?.id, asText(w?.text)]))
      const words = (o.correctOrder ?? []).map((id: string) => byId.get(id)).filter(Boolean)
      return asExpected(words.length ? words.join(" ") : null)
    }
    case "spelling_drop":
    case "punctuation_drop":
    case "column_match": {
      const parts = o.pairs ?? o.blanks
      const part = find(parts, targetRef) ?? only(parts)
      const item = find(o.bank, String(part?.correctItemId ?? ""))
      return item ? fromHtml(item.text) : null
    }
    case "image_sort": {
      // Per item: which group it belonged in. The item's own text names which item that was.
      const item = find(o.items, targetRef)
      const category = find(o.categories, String(item?.categoryId ?? ""))
      if (!item || !category) return null
      return asExpected(`${asText(item.text)} → ${asText(category.name)}`)
    }
    case "text_select":
      return asExpected(joinAccepted((o.answers ?? []).map((a: any) => a?.value)) || null)
    case "punctuation_insert": {
      // `offset` indexes into `sentence` and the mark renders immediately BEFORE the character
      // at that index, so the sentence can be rebuilt with the marks put in. Offsets are
      // applied back to front, or each insertion would shift the ones after it.
      const sentence = String(o.sentence ?? "")
      const sites = [...(o.sites ?? [])]
        .filter((site: any) => Number.isFinite(Number(site?.offset)))
        .sort((a: any, b: any) => Number(b.offset) - Number(a.offset))
      if (!sentence || sites.length === 0) return null
      return asExpected(
        sites.reduce((text: string, site: any) => {
          const at = Math.max(0, Math.min(text.length, Number(site.offset)))
          return text.slice(0, at) + (site.marks ?? []).join("") + text.slice(at)
        }, sentence),
      )
    }
    case "reading_comp": {
      const sub = find(o.subQuestions, targetRef)
      if (!sub) return null
      // A sub-question is either open ("answers") or multiple choice ("choices").
      if (Array.isArray(sub.choices)) {
        return fromChoice(sub.choices, (c) => c?.isAnswer)
      }
      return asExpected(joinAccepted((sub.answers as unknown[]) ?? []) || null)
    }
    default:
      return null
  }
}

/** One part's verdict, with what the author said about that mistake. */
const AnswerRow: React.FC<{
  question: EngReportReviewQuestion
  answer: EngReportAnswer
}> = ({ question, answer }) => {
  // Shown wherever the student did not get it right - wrong, skipped, or still awaiting a
  // tutor, which is the case that needs it most, since the tutor is about to mark it. A part
  // the student got right needs no answer key beside it.
  const expected =
    answer.is_correct === true ? null : expectedAnswer(question, answer.target_ref)
  return (
  <div className="d-flex align-items-start justify-content-between border-top py-2">
    <div className="flex-grow-1 me-3">
      <div className="font-size-13">{partLabel(question, answer.target_ref)}</div>
      {expected ? (
        <div className="font-size-12 mt-1">
          <span className="text-muted">Expected: </span>
          {expected.option ? (
            <span className="fw-semibold me-1" style={{ color: "#15803d" }}>
              Option {expected.option}
              {expected.kind === "text" ? " · " : ""}
            </span>
          ) : null}
          {expected.kind === "text" ? (
            <span className="fw-semibold" style={{ color: "#15803d" }}>
              {expected.text}
            </span>
          ) : expected.kind === "image" ? (
            <img
              src={expected.src}
              alt="The expected answer"
              className="rounded border d-inline-block align-middle"
              style={{ maxHeight: 72, maxWidth: 180 }}
            />
          ) : (
            <span className="fw-semibold" style={{ color: "#15803d" }}>
              <i className="fas fa-volume-up me-1" aria-hidden="true"></i>
              {expected.name}
            </span>
          )}
        </div>
      ) : null}
      {/* The skill is carried per ANSWER because a reading comprehension tests a different one
          in each sub-question. Shown only when it differs from the question's own skill, so an
          ordinary single-skill question does not repeat itself. */}
      {answer.skill_name && answer.skill_name !== question.skill_name ? (
        <div className="text-muted font-size-12">{answer.skill_name}</div>
      ) : null}
      {answer.misconception_label ? (
        <div className="text-muted font-size-12">
          Misconception: {answer.misconception_label}
        </div>
      ) : null}
      {/* The author's note on the catalogued wrong answer the student matched - why this
          particular mistake gets made. The most useful line on the page for a tutor. */}
      {answer.wrong_answer_note ? (
        <div className="font-size-12 mt-1">{answer.wrong_answer_note}</div>
      ) : null}
    </div>
    <div className="text-end">
      <VerdictBadge answer={answer} />
      {answer.duration_seconds != null ? (
        <div className="text-muted font-size-12 mt-1">{answer.duration_seconds}s</div>
      ) : null}
    </div>
  </div>
  )
}

const QuestionCard: React.FC<{ question: EngReportReviewQuestion; index: number }> = ({
  question,
  index,
}) => {
  // A slot with no attempt row was never served - the test ended, or is still running - and says
  // so rather than being hidden, which would make the block look shorter than it was.
  if (question.answers.length === 0) {
    return (
      <div className="rounded border p-3 mb-3 bg-light">
        <div className="text-muted font-size-13">
          Question #{index + 1}: not attempted
          {question.skill_name ? ` · ${question.skill_name}` : ""}
        </div>
      </div>
    )
  }

  return (
    <div className="rounded border p-3 mb-3 bg-white">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="text-muted text-uppercase font-size-12">
          Question #{index + 1} · {typeLabel(question.snapshot.type)}
        </div>
        {question.is_manually_marked ? (
          <span className="badge bg-info">Marked by a tutor</span>
        ) : null}
      </div>

      {question.skill_name ? (
        <div className="text-muted font-size-12 mb-2">
          {[question.skill_name, question.sub_topic_name, question.year_level_name]
            .filter(Boolean)
            .join(" · ")}
        </div>
      ) : null}

      {/* The question as the child saw it, with their own answers in place. */}
      <EngDiagQuestionView
        question={toQuestionView(question)}
        value={answersFor(question)}
        readOnly
      />

      <div className="mt-3">
        {question.answers.map(answer => (
          <AnswerRow key={answer.pk_attempt_key} question={question} answer={answer} />
        ))}
      </div>
    </div>
  )
}

export type EngDiagAttemptReviewModalProps = {
  isOpen: boolean
  onClose: () => void
  sessionTopicKey: string | null
}

export const EngDiagAttemptReviewModal: React.FC<EngDiagAttemptReviewModalProps> = ({
  isOpen,
  onClose,
  sessionTopicKey,
}) => {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [data, setData] = React.useState<GetEngTopicAttemptDetailResponse | null>(null)

  React.useEffect(() => {
    if (!isOpen || !sessionTopicKey) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    setData(null)
    getEngTopicAttemptDetail({ session_topic_key: sessionTopicKey }, controller.signal)
      .then(response => {
        if (controller.signal.aborted) return
        // The envelope reports its own failures in `error` with status 0; an exception never
        // happens for "no access" or "topic not found", so both have to be read off the body.
        if (response.status !== 1) {
          setError(response.error || "Couldn't load this topic")
        } else {
          setData(response)
        }
        setLoading(false)
      })
      .catch(err => {
        if (controller.signal.aborted) return
        setError(err?.message || "Couldn't load this topic")
        setLoading(false)
      })
    return () => controller.abort()
  }, [isOpen, sessionTopicKey])

  // Grouped by phase in the order the server returned, which is the order the student met them.
  const grouped: { phase: string; items: EngReportReviewQuestion[] }[] = []
  if (data) {
    for (const question of data.questions) {
      const last = grouped[grouped.length - 1]
      if (last && last.phase === question.phase) last.items.push(question)
      else grouped.push({ phase: question.phase, items: [question] })
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" scrollable>
      <ModalHeader toggle={onClose}>
        {data ? (
          <span>
            {data.topic.topic_name}{" "}
            <span className="text-muted font-size-13">
              {[
                data.topic.cluster_name,
                data.topic.year_level_name,
                formatDate(data.topic.attempted_on),
              ]
                .filter(Boolean)
                .join(" · ")}
              {data.topic.is_prereq ? " · Prerequisite test" : ""}
            </span>
          </span>
        ) : (
          <span>Attempt Review</span>
        )}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : error ? (
          <p className="text-danger mb-0">{error}</p>
        ) : !data ? null : (
          <>
            <div className="mb-3 d-flex flex-wrap align-items-center gap-4">
              <div>
                <div className="text-muted text-uppercase font-size-12">Score</div>
                <div className="fw-bold font-size-16">
                  {data.totals.correct}/{data.totals.total}{" "}
                  <span className="text-muted font-size-13">
                    ({data.totals.accuracy_pct}%)
                  </span>
                </div>
              </div>
              {/* Answers, not questions: an English question can be marked in several parts, so
                  the score above is out of parts and would otherwise read as a question count. */}
              <div>
                <div className="text-muted text-uppercase font-size-12">Marked parts</div>
                <div className="fw-bold font-size-16">{data.totals.total}</div>
              </div>
              {data.totals.awaiting_marking > 0 ? (
                <div>
                  <div className="text-muted text-uppercase font-size-12">
                    Awaiting marking
                  </div>
                  <div className="fw-bold font-size-16">
                    {data.totals.awaiting_marking}
                  </div>
                </div>
              ) : null}
              <div>
                <div className="text-muted text-uppercase font-size-12">Status</div>
                <div className="fw-bold font-size-16">{data.topic.topic_status}</div>
              </div>
            </div>

            {grouped.length === 0 ? (
              <p className="text-muted mb-0">No questions recorded for this topic.</p>
            ) : (
              grouped.map((group, groupIndex) => (
                <div key={groupIndex} className="mb-4">
                  <div className="text-uppercase fw-bold text-muted font-size-12 mb-2">
                    {phaseLabel(group.phase)}
                  </div>
                  {group.items.map((question, index) => (
                    <QuestionCard
                      key={question.pk_session_question_key}
                      question={question}
                      index={index}
                    />
                  ))}
                </div>
              ))
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  )
}

export default EngDiagAttemptReviewModal
